import {
  type AnnouncementBannerItem,
  AnnouncementContractError,
  type AnnouncementLevel,
  type AnnouncementMeta,
  createAnnouncementBannerPath,
  createAnnouncementDetailPath as createAnnouncementDetailContractPath,
  createAnnouncementsPath as createAnnouncementsContractPath,
  decodeAnnouncement,
  decodeAnnouncementMeta,
  decodeAnnouncements,
  decodeBanners,
  type PublicAnnouncement,
} from "@nijiurachan/announce-contract";

/**
 * apps/notify（パッケージ名 `aimg-announce`）の
 * 公開GET API向けクライアント。既存の`apiGet`とはレスポンス封筒が異なるため、
 * この機能内でHTTP・timeout・envelopeの境界を一元化する。
 */

/** ローカルdev（`bun run dev` 起動のaimg-notify）の既定オリジン。 */
const ANNOUNCE_DEV_DEFAULT = "http://127.0.0.1:8787";

/** 公開GET API共通の取得タイムアウト。 */
export const ANNOUNCE_FETCH_TIMEOUT_MS = 8_000;

export interface AnnounceRequestOptions {
  signal?: AbortSignal;
  timeoutMs?: number;
}

export type AnnounceRequestErrorKind =
  | "aborted"
  | "challenge"
  | "http"
  | "invalid_response"
  | "network"
  | "not_found"
  | "timeout";

export class AnnounceRequestError extends Error {
  public readonly kind: AnnounceRequestErrorKind;
  public readonly status: number | undefined;

  constructor(kind: AnnounceRequestErrorKind, status?: number) {
    super(kind);
    this.name = "AnnounceRequestError";
    this.kind = kind;
    this.status = status;
  }
}

/**
 * 末尾のスラッシュを除去する。環境変数に末尾スラッシュがあっても、
 * パス結合時に二重スラッシュを作らない。
 */
export function stripTrailingSlashes(url: string): string {
  return url.replace(/\/+$/, "");
}

export const ANNOUNCE_BASE_URL: string = stripTrailingSlashes(
  import.meta.env.VITE_ANNOUNCE_BASE_URL ||
    (import.meta.env.DEV
      ? ANNOUNCE_DEV_DEFAULT
      : "https://announce.nijiurachan.net"),
);

export type AnnounceLevel = AnnouncementLevel;
export type AnnounceBannerItem = AnnouncementBannerItem;
export type { AnnouncementMeta, PublicAnnouncement };

export interface AnnouncementListSnapshot {
  announcements: PublicAnnouncement[];
  /** Global read receipt only for a complete list; null for partial or older responses. */
  articleUnreadRevision: number | null;
}

function invalidResponse(error: unknown): never {
  if (error instanceof AnnouncementContractError) {
    throw new AnnounceRequestError("invalid_response");
  }
  throw error;
}

function preserveValidItems<T>(decoded: {
  data: T[];
  issues: readonly unknown[];
}): T[] {
  if (decoded.data.length === 0 && decoded.issues.length > 0) {
    throw new AnnounceRequestError("invalid_response");
  }
  return decoded.data;
}

/** `GET /api/v1/banner`を解析する。不正要素だけを安全に除外する。 */
export function parseAnnounceBannerPayload(
  payload: unknown,
): AnnounceBannerItem[] {
  try {
    return preserveValidItems(decodeBanners(payload));
  } catch (error) {
    return invalidResponse(error);
  }
}

/**
 * 記事一覧を解析する。不正封筒は失敗、不正要素だけを安全に除外する。
 */
export function parseAnnouncementsPayload(
  payload: unknown,
  window?: { limit: number; offset: number },
): AnnouncementListSnapshot {
  try {
    const decoded = decodeAnnouncements(payload);
    return {
      announcements: preserveValidItems(decoded),
      articleUnreadRevision:
        decoded.issues.length === 0 &&
        window !== undefined &&
        window.offset === 0 &&
        decoded.data.length < window.limit
          ? (decoded.article_unread_revision ?? null)
          : null,
    };
  } catch (error) {
    return invalidResponse(error);
  }
}

/** 記事詳細を解析する。不正フィールドが1つでもあれば失敗扱いにする。 */
export function parseAnnouncementPayload(payload: unknown): PublicAnnouncement {
  try {
    return decodeAnnouncement(payload);
  } catch (error) {
    return invalidResponse(error);
  }
}

/** 新着判定用metaを解析する。日時・revisionとも実行時に検証する。 */
export function parseAnnouncementMetaPayload(
  payload: unknown,
): AnnouncementMeta {
  try {
    return decodeAnnouncementMeta(payload);
  } catch (error) {
    return invalidResponse(error);
  }
}

export function createAnnouncementsPath(
  limit: number,
  offset: number,
  revision?: number,
): string {
  return createAnnouncementsContractPath(limit, offset, revision);
}

export function createAnnouncementDetailPath(
  id: number,
  revision?: number,
): string {
  return createAnnouncementDetailContractPath(id, revision);
}

async function requestAnnounceJson(
  path: string,
  options: AnnounceRequestOptions = {},
): Promise<unknown> {
  const { signal, timeoutMs = ANNOUNCE_FETCH_TIMEOUT_MS } = options;
  const timeoutController = new AbortController();
  const timeoutId = setTimeout(() => timeoutController.abort(), timeoutMs);
  const combinedSignal = signal
    ? AbortSignal.any([signal, timeoutController.signal])
    : timeoutController.signal;

  try {
    const response = await fetch(`${ANNOUNCE_BASE_URL}${path}`, {
      credentials: "omit",
      signal: combinedSignal,
    });
    if (!response.ok) {
      const isCloudflareChallenge =
        response.status === 403 &&
        response.headers.get("cf-mitigated")?.toLowerCase() === "challenge";
      let kind: AnnounceRequestErrorKind = "http";
      if (response.status === 404) kind = "not_found";
      else if (isCloudflareChallenge) kind = "challenge";
      throw new AnnounceRequestError(kind, response.status);
    }
    try {
      return (await response.json()) as unknown;
    } catch {
      throw new AnnounceRequestError("invalid_response", response.status);
    }
  } catch (error: unknown) {
    if (error instanceof AnnounceRequestError) throw error;
    if (signal?.aborted) {
      throw new AnnounceRequestError("aborted");
    }
    if (timeoutController.signal.aborted) {
      throw new AnnounceRequestError("timeout");
    }
    throw new AnnounceRequestError("network");
  } finally {
    clearTimeout(timeoutId);
  }
}

/** バナーチャンネルを取得する。失敗と正常な0件を呼び出し側で区別する。 */
export async function fetchAnnounceBanners(
  signal?: AbortSignal,
  revision?: number,
): Promise<AnnounceBannerItem[]> {
  const payload = await requestAnnounceJson(
    createAnnouncementBannerPath(5, revision),
    {
      signal,
    },
  );
  return parseAnnounceBannerPayload(payload);
}

export async function fetchAnnouncements(
  limit: number,
  offset: number,
  options?: AnnounceRequestOptions,
  revision?: number,
): Promise<AnnouncementListSnapshot> {
  const path = createAnnouncementsPath(limit, offset, revision);
  return parseAnnouncementsPayload(await requestAnnounceJson(path, options), {
    limit,
    offset,
  });
}

export async function fetchAnnouncement(
  id: number,
  options?: AnnounceRequestOptions,
  revision?: number,
): Promise<PublicAnnouncement> {
  const path = createAnnouncementDetailPath(id, revision);
  return parseAnnouncementPayload(await requestAnnounceJson(path, options));
}

export async function fetchAnnouncementMeta(
  options?: AnnounceRequestOptions,
): Promise<AnnouncementMeta> {
  return parseAnnouncementMetaPayload(
    await requestAnnounceJson("/api/v1/meta", options),
  );
}
